import React from 'react';
import { type ProductCombination, type ProductField, type SelectedOptions } from '../../../type';
import { DollarSign, Package2 } from 'lucide-react';

interface CombinationsTableProps {
  combinations: ProductCombination[];
  fields: ProductField[];
  selectedOptions: SelectedOptions;
  onPriceChange: (combinationId: string, price: number) => void;
}

const CombinationsTable: React.FC<CombinationsTableProps> = ({
  combinations,
  fields,
  selectedOptions,
  onPriceChange,
}) => {
  const selectedFields = fields.filter(field => selectedOptions[field.id]?.length > 0);
  const handlePriceChange = (
    combinationId: string,
    value: string
  ) => {
    const price = parseFloat(value) || 0;
    onPriceChange(combinationId, price);
  };

  if (combinations.length === 0) {
    return (
      <div className="rounded-xl border bg-background px-6 py-14">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border bg-muted">
            <Package2 className="h-5 w-5 text-muted-foreground" />
          </div>

          <h3 className="text-base font-semibold">
            No combinations generated
          </h3>

          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            Add product options above to generate product
            combinations automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-4">
        <div>
          <h2 className="text-sm font-semibold tracking-tight">
            Product Combinations
          </h2>

          <p className="mt-1 text-sm text-muted-foreground">
            {combinations.length} combinations generated
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="border-b">
              <th className="h-11 px-6 text-left font-medium text-muted-foreground w-14">
                #
              </th>

              {selectedFields.map((field) => (
                <th
                  key={field.id}
                  className="h-11 px-6 text-left font-medium text-muted-foreground whitespace-nowrap"
                >
                  {field.label}
                </th>
              ))}

              <th className="h-11 px-6 text-left font-medium text-muted-foreground w-44">
                Price
              </th>
            </tr>
          </thead>

          <tbody>
            {combinations.map((combination, index) => (
              <tr
                key={combination.id}
                className="border-b transition-colors hover:bg-muted/30"
              >
                {/* Index */}
                <td className="px-6 py-4 font-medium text-foreground">
                  {index + 1}
                </td>

                {/* Combination Values */}
                {selectedFields.map((field) => {
                  const value =
                    combination.combination[
                    field.label
                      .toLowerCase()
                      .replace(/\s+/g, '')
                    ];

                  return (
                    <td
                      key={field.id}
                      className="px-6 py-4 whitespace-nowrap"
                    >
                      <div className="inline-flex items-center rounded-md border bg-muted/40 px-2.5 py-1 text-xs font-medium">
                        {value || '-'}
                      </div>
                    </td>
                  );
                })}

                {/* Price */}
                <td className="px-6 py-4">
                  <div className="relative min-w-[140px]">
                    <DollarSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={combination.price}
                      onChange={(e) =>
                        handlePriceChange(
                          combination.id,
                          e.target.value
                        )
                      }
                      placeholder="0.00"
                      className="
                        flex
                        h-9
                        w-full
                        rounded-md
                        border
                        border-input
                        bg-background
                        pl-9
                        pr-3
                        py-2
                        text-sm
                        ring-offset-background
                        file:border-0
                        file:bg-transparent
                        file:text-sm
                        file:font-medium
                        placeholder:text-muted-foreground
                        focus-visible:outline-none
                        focus-visible:ring-2
                        focus-visible:ring-ring
                        focus-visible:ring-offset-2
                        disabled:cursor-not-allowed
                        disabled:opacity-50
                      "
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CombinationsTable;